import { CoinsStacked03, Play, Rocket01, Stars02, Tag01, XCircle } from "@untitledui/icons";
import type { CandidateClassification } from "@/api/candidates";
import { cx } from "@/utils/cx";

type ClassificationConfig = {
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
};

const CLASSIFICATION_CONFIG: Record<CandidateClassification, ClassificationConfig> = {
  strong_candidate: {
    icon: <Rocket01 className="w-5 h-5" />,
    title: "Strong partner Candidate",
    description: "Excellent category fit and strong engagement. Low risk, high potential, ideal new affiliate.",
    gradient: "bg-gradient-to-r from-lime-200/20 to-green-500/20",
  },
  worth_considering: {
    icon: <Stars02 className="w-5 h-5" />,
    title: "Worth Considering",
    description: "Moderate results and decent fit. Some risk factors, suitable for testing or limited campaigns.",
    gradient: "bg-gradient-to-r from-purple-200/20 to-purple-500/20",
  },
  recommended_pass: {
    icon: <XCircle className="w-5 h-5" />,
    title: "Recommended Pass",
    description: "Low activity, poor category fit, or safety issues. Not aligned with your brand at this stage.",
    gradient: "bg-gradient-to-r from-violet-200/20 to-violet-500/20",
  },
};

type StatItem = {
  icon: React.ReactNode;
  label: string;
};

function getPostingLabel(ecVideoCount: number): string {
  if (ecVideoCount >= 15) return "Frequent posting";
  if (ecVideoCount >= 10) return "Moderate posting";
  if (ecVideoCount >= 5) return "Low posting";
  return "Low posting";
}

type CandidateClassificationCardProps = {
  classification: CandidateClassification;
  topCategory?: string;
  ecVideoCount: number;
  gmvAmount: number;
  currencyFormatter: Intl.NumberFormat;
  /** When true, hides the title/description header and shows only stats */
  compact?: boolean;
};

export const CandidateClassificationCard = ({
  classification,
  topCategory,
  ecVideoCount,
  gmvAmount,
  currencyFormatter,
  compact = false,
}: CandidateClassificationCardProps) => {
  const config = CLASSIFICATION_CONFIG[classification];

  const stats: StatItem[] = [
    ...(topCategory
      ? [
          {
            icon: <Tag01 className="w-5 h-5 text-primary" />,
            label: `${topCategory} content`,
          },
        ]
      : []),
    {
      icon: <Play className="w-5 h-5 text-primary" />,
      label: `${getPostingLabel(ecVideoCount)} (${ecVideoCount}/mo)`,
    },
    {
      icon: <CoinsStacked03 className="w-5 h-5 text-primary" />,
      label: `Averages ${currencyFormatter.format(gmvAmount)}/month GMV`,
    },
  ];

  if (compact) {
    return (
      <div className={cx("rounded-lg px-5 py-5 flex flex-col gap-3", config.gradient)}>
        {stats.map((stat, index) => (
          <div key={index} className="flex items-center gap-2">
            {stat.icon}
            <span className="text-sm text-primary leading-5">{stat.label}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cx("rounded-lg px-10 py-8 flex justify-between items-start gap-8", config.gradient)}>
      {/* Left side - Icon, Title, Description */}
      <div className="flex flex-col gap-4 max-w-80">
        <div className="text-primary">{config.icon}</div>
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-medium text-primary leading-7">{config.title}</h3>
          <p className="text-sm text-secondary leading-5">{config.description}</p>
        </div>
      </div>

      {/* Right side - Stats */}
      <div className="flex flex-col gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="flex items-center gap-1.5">
            {stat.icon}
            <span className="text-sm text-primary leading-5">{stat.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
